import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cts_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      // Only redirect if token existed (not during intentional logout)
      const hadToken = localStorage.getItem('cts_token');
      localStorage.removeItem('cts_token');
      localStorage.removeItem('cts_user');
      if (hadToken && !window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  validateInvite: (token) => api.get(`/auth/invite/${token}`),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  validateResetToken: (token) => api.get(`/auth/reset-password/${token}`),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

export const inventoryAPI = {
  list: (params) => api.get('/inventory', { params }),
  get: (id) => api.get(`/inventory/${id}`),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  bulkDelete: (ids) => api.post('/inventory/bulk-delete', { ids }),
  generateDescription: (data) => api.post('/inventory/generate-description', data),
  compare: (ids) => api.post('/inventory/compare', ids),
  vinDecode: (vin) => api.get(`/vin-decode/${vin}`),
  getStatuses: () => api.get('/inventory-statuses'),
  addStatus: (status) => api.post('/inventory-statuses', { status }),
  sendListing: (id, data) => api.post(`/inventory/${id}/send-listing`, data),
  sendDocument: (id, data) => api.post(`/inventory/${id}/send-document`, data),
  listDocs: (id) => api.get(`/inventory/${id}/docs`),
  uploadDoc: (id, file, docType) => { const fd = new FormData(); fd.append('file', file); return api.post(`/inventory/${id}/docs/upload?doc_type=${docType}`, fd); },
  deleteDoc: (id, docId) => api.delete(`/inventory/${id}/docs/${docId}`),
};

export const leadsAPI = {
  create: (data) => api.post('/leads', data),
  list: () => api.get('/leads'),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  bulkDelete: (ids) => api.post('/leads/bulk-delete', { ids }),
  sendInventory: (id, inventoryIds) => api.post(`/leads/${id}/send-inventory`, inventoryIds),
  convert: (id) => api.post(`/leads/${id}/convert`),
};

export const clientsAPI = {
  list: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  bulkDelete: (ids) => api.post('/clients/bulk-delete', { ids }),
  portfolio: (id) => api.get(`/clients/${id}/portfolio`),
  sendInventory: (id, inventoryIds) => api.post(`/clients/${id}/send-inventory`, inventoryIds),
  sendDocument: (id, documentId) => api.post(`/clients/${id}/send-document?document_id=${documentId}`),
  sendEsign: (id, dealId) => api.post(`/clients/${id}/send-esign?deal_id=${dealId}`),
  sendInvite: (id) => api.post(`/clients/${id}/send-invite`),
};

export const paymentsAPI = {
  createHoldDepositLink: (data) => api.post('/payments/create-hold-deposit-link', data),
  markReceived: (id) => api.post(`/payments/${id}/mark-received`),
  list: (params) => api.get('/payments', { params }),
};

export const dealsAPI = {
  list: (params) => api.get('/deals', { params }),
  get: (id) => api.get(`/deals/${id}`),
  create: (data) => api.post('/deals', data),
  update: (id, data) => api.put(`/deals/${id}`, data),
  delete: (id) => api.delete(`/deals/${id}`),
  bulkDelete: (ids) => api.post('/deals/bulk-delete', { ids }),
  addNote: (id, data) => api.post(`/deals/${id}/notes`, data),
  getNotes: (id) => api.get(`/deals/${id}/notes`),
  deleteNote: (dealId, noteId) => api.delete(`/deals/${dealId}/notes/${noteId}`),
  submitToCredit: (data) => api.post('/deals/submit-to-credit', data),
  generateBrokerNotes: (data) => api.post('/deals/generate-broker-notes', data),
  addChatNote: (id, data) => api.post(`/deals/${id}/chat-notes`, data),
  getChatNotes: (id) => api.get(`/deals/${id}/chat-notes`),
  uploadDoc: (id, file, docType) => { const fd = new FormData(); fd.append('file', file); return api.post(`/deals/${id}/upload-doc?doc_type=${docType}`, fd); },
  deleteDealDoc: (dealId, docId) => api.delete(`/deals/${dealId}/documents/${docId}`),
  updateDocCategory: (dealId, docId, docType) => api.patch(`/deals/${dealId}/documents/${docId}/category`, { doc_type: docType }),
  listDocs: (id) => api.get(`/deals/${id}/documents`),
  downloadDoc: (id, docId) => api.get(`/deals/${id}/download-doc/${docId}`, { responseType: 'blob' }),
  downloadZip: (id) => api.get(`/deals/${id}/download-zip`, { responseType: 'blob' }),
  sendPackage: (id, data) => api.post(`/deals/${id}/send-package`, data),
  getActivity: (id) => api.get(`/deals/${id}/activity`),
  getBillOfSale: (id) => `${API_URL}/api/deals/${id}/bill-of-sale`,
  sendBillOfSale: (id) => api.post(`/deals/${id}/send-bill-of-sale`),
  generateBos: (id, data) => api.post(`/deals/${id}/generate-bos`, data),
  lenderPackagePreview: (id) => api.get(`/deals/${id}/lender-package/preview`),
  lenderPackageDownloadUrl: (id) => `${API_URL}/api/deals/${id}/lender-package/download`,
};

export const esignAPI = {
  create: (data) => api.post('/esign/create', data),
  get: (token) => api.get(`/esign/${token}`),
  sign: (token, data) => api.post(`/esign/${token}/sign`, data),
  getDealRequests: (dealId) => api.get(`/esign/deal/${dealId}`),
  getTemplate: () => api.get('/esign/template'),
  updateTemplate: (data) => api.put('/esign/template', data),
};

export const docTemplatesAPI = {
  list: () => api.get('/doc-templates'),
  get: (id) => api.get(`/doc-templates/${id}`),
  create: () => api.post('/doc-templates'),
  update: (id, data) => api.put(`/doc-templates/${id}`, data),
  remove: (id) => api.delete(`/doc-templates/${id}`),
  uploadPdf: (id, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/doc-templates/${id}/upload-pdf`, fd); },
  deletePdf: (id) => api.delete(`/doc-templates/${id}/pdf`),
  getDataTags: () => api.get('/doc-templates/data-tags'),
  generate: (id, data) => api.post(`/doc-templates/${id}/generate`, data, { responseType: 'blob' }),
  generateAndSend: (id, data) => api.post(`/doc-templates/${id}/generate-and-send`, data),
};

export const docSignAPI = {
  create: (data) => api.post('/doc-sign/create', data),
  get: (token) => api.get(`/doc-sign/${token}`),
  sign: (token, data) => api.post(`/doc-sign/${token}/sign`, data),
  getAudit: (token) => api.get(`/doc-sign/${token}/audit`),
  getDealRequests: (dealId) => api.get(`/doc-sign/deal/${dealId}`),
};

export const financingAPI = {
  submit: (data) => api.post('/financing', data),
  list: () => api.get('/financing'),
  update: (id, data) => api.put(`/financing/${id}`, data),
  updateStatus: (id, status) => api.put(`/financing/${id}`, { status }),
  delete: (id) => api.delete(`/financing/${id}`),
  bulkDelete: (ids) => api.post('/financing/bulk-delete', { ids }),
};

export const staffAPI = {
  list: () => api.get('/staff'),
  listForDropdown: () => api.get('/staff-list'),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
  bulkDelete: (ids) => api.post('/staff/bulk-delete', { ids }),
  loginAs: (id) => api.post(`/staff/${id}/login-as`),
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  testSms: (phone) => api.post('/settings/test-sms', { phone }),
  uploadLogo: (type, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/settings/upload-logo?logo_type=${type}`, fd);
  },
  getBranding: () => api.get('/branding'),
};

export const cmsAPI = {
  get: (key) => api.get(`/cms/${key}`),
  update: (key, data) => api.put(`/cms/${key}`, data),
};

export const pagesAPI = {
  get: (slug) => api.get(`/pages/${slug}`),
  save: (slug, data) => api.put(`/pages/${slug}`, data),
  list: () => api.get('/pages'),
};

export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
};

export const seedAPI = {
  seed: () => api.post('/seed'),
};

export const uploadAPI = {
  upload: (category, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/upload/${category}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const documentsAPI = {
  generate: (data) => api.post('/documents/generate', data),
  generateBillOfSale: (data) => api.post('/documents/generate-bill-of-sale', data),
  list: (params) => api.get('/documents', { params }),
  delete: (id) => api.delete(`/documents/${id}`),
  bulkDelete: (ids) => api.post('/documents/bulk-delete', { ids }),
  resend: (id, data) => api.post(`/documents/${id}/resend`, data || {}),
};

export const uploadLinksAPI = {
  generate: (clientId, dealId) => api.post(`/upload-links/generate?client_id=${clientId}&deal_id=${dealId}`),
  sendPreApproval: (clientId, dealId, equipmentInfo) => api.post(`/upload-links/pre-approval?client_id=${clientId}&deal_id=${dealId}&equipment_info=${encodeURIComponent(equipmentInfo || '')}`),
  validate: (token) => api.get(`/upload-links/${token}`),
  upload: (token, file, docType) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = docType ? `?doc_type=${encodeURIComponent(docType)}` : '';
    return api.post(`/upload-links/${token}/upload${params}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const lendersAPI = {
  list: () => api.get('/lenders'),
  create: (data) => api.post('/lenders', data),
  update: (id, data) => api.put(`/lenders/${id}`, data),
  delete: (id) => api.delete(`/lenders/${id}`),
  bulkDelete: (ids) => api.post('/lenders/bulk-delete', { ids }),
  generateComments: (lenderId, dealId) => api.post(`/lenders/${lenderId}/generate-comments?deal_id=${dealId}`),
};

export const notificationsAPI = {
  list: (params) => api.get('/notifications', { params }),
  update: (id, data) => api.put(`/notifications/${id}`, data),
  delete: (id) => api.delete(`/notifications/${id}`),
  bulkDelete: (ids) => api.post('/notifications/bulk-delete', { ids }),
  deleteAll: (params) => api.delete('/notifications', { params }),
  matchInventory: (inventoryId) => api.post(`/notifications/match-inventory/${inventoryId}`),
};

export const clientPortalAPI = {
  profile: () => api.get('/client/profile'),
  deals: () => api.get('/client/deals'),
  documents: () => api.get('/client/documents'),
  notifications: () => api.get('/client/notifications'),
  savedSearches: () => api.get('/client/saved-searches'),
  saveSearch: (data) => api.post('/client/saved-searches', data),
  deleteSavedSearch: (id) => api.delete(`/client/saved-searches/${id}`),
};

export const prospectsAPI = {
  upload: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/prospects/upload', fd); },
  list: (params) => api.get('/prospects', { params }),
  update: (id, data) => api.put(`/prospects/${id}`, data),
  delete: (id) => api.delete(`/prospects/${id}`),
  bulkDelete: (ids) => api.post('/prospects/bulk-delete', { ids }),
  deleteAll: () => api.delete('/prospects/delete-all'),
  deleteBatch: (batchId) => api.delete(`/prospects/delete-batch/${batchId}`),
  batches: () => api.get('/prospects/batches'),
  sendAlert: (data) => api.post('/prospects/send-alert', data),
  toggleAlerts: (data) => api.post('/prospects/toggle-alerts', data),
  testEmail: (to_email) => api.post('/prospects/test-email', { to_email }),
  emailActivity: () => api.get('/prospects/email-activity'),
  exportExcel: () => api.get('/prospects/export', { responseType: 'blob' }),
};

export const socialAPI = {
  getSettings: () => api.get('/social/settings'),
  updateSettings: (data) => api.put('/social/settings', data),
  generatePost: (data) => api.post('/social/generate-post', data),
  publish: (data) => api.post('/social/publish', data),
  getCampaigns: () => api.get('/social/campaigns'),
  createCampaign: (data) => api.post('/social/campaigns', data),
  getPosts: () => api.get('/social/posts'),
};

export default api;
